import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Logo, useBranding } from '../contexts/BrandingContext';

/**
 * Anmeldung laeuft AUSSCHLIESSLICH ueber PowerID (Authentik-OIDC), und zwar
 * ueber das Backend: /api/auth/oidc/login → PowerID → Callback setzt das
 * Session-Cookie. Ein Passwortformular gibt es nicht.
 *
 * Schon angemeldet → weiter zur Zielroute. Sonst DIREKT zu PowerID — ein
 * Zwischenschritt mit einem einzigen Knopf ist ein Klick ohne Entscheidung.
 * Zwei Faelle bleiben auf dieser Seite, sonst sperrt sie den Benutzer aus:
 *   • nach dem Abmelden (?abgemeldet=1) — sofortiges Weiterleiten meldete
 *     einen augenblicklich wieder an;
 *   • bei einem Fehler (?error=…) — die Meldung waere sonst unsichtbar.
 */
const ERROR_TEXT: Record<string, string> = {
  kein_zugriff: 'Ihr PowerID-Konto ist für diese Anwendung nicht freigeschaltet. Bitte wenden Sie sich an die IT Ihrer Firma.',
  deaktiviert: 'Dieses Benutzerkonto ist deaktiviert.',
  powerid_down: 'PowerID ist gerade nicht erreichbar. Bitte in ein paar Minuten erneut versuchen.',
  oidc: 'Anmeldung fehlgeschlagen — bitte erneut versuchen.',
};

export default function Login() {
  const { isAuthenticated, isLoading } = useAuth();
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const next = params.get('next') ?? '/';
  const abgemeldet = !!params.get('abgemeldet');
  const error = params.get('error');
  const errorMsg = error ? (ERROR_TEXT[error] ?? ERROR_TEXT.oidc) : null;

  const loginWithPowerID = () => {
    setBusy(true);
    window.location.href = `/api/auth/oidc/login?next=${encodeURIComponent(next)}`;
  };

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    if (error || abgemeldet) return;
    loginWithPowerID();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  if (!isLoading && isAuthenticated) return <Navigate to={next} replace />;

  return (
    <div className="flex min-h-dvh flex-col bg-gray-50">
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-center border-b border-gray-200 bg-white px-6 py-5">
            <Logo className="h-16" textClass="text-3xl" />
          </div>
          <div className="space-y-4 p-6">
            {abgemeldet && (
              <div className="rounded-md bg-ps-dark-50 p-3 text-center text-[13px] text-ps-dark-600">Sie wurden abgemeldet.</div>
            )}
            {errorMsg && (
              <div className="rounded-md bg-rose-50 p-3 text-[13px] text-rose-700">{errorMsg}</div>
            )}
            <p className="text-center text-[13px] leading-relaxed text-ps-dark-500">
              Die Anmeldung erfolgt über das zentrale <span className="font-semibold text-ps-dark-700">PowerID</span>-Konto.
            </p>
            <button onClick={loginWithPowerID} disabled={busy || isLoading}
              className="btn-primary flex w-full items-center justify-center gap-2 !py-2.5">
              {busy ? (
                <><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Weiterleitung zu PowerID …</>
              ) : (
                <><UserCircleIcon className="h-4 w-4" /> Mit PowerID anmelden</>
              )}
            </button>
            <div className="border-t border-gray-200 pt-3">
              <p className="text-center text-[11.5px] text-ps-dark-400">Kein Zugriff? Die Freischaltung erfolgt durch die IT Ihrer Firma in PowerID.</p>
            </div>
          </div>
        </div>
      </div>
      <footer className="border-t border-gray-200 bg-white">
        <div className="w-full px-4 py-3 text-center text-[11px] text-ps-dark-400">{branding.name}{branding.untertitel ? ` · ${branding.untertitel}` : ''} · Ihre Dateien bleiben bei Ihnen</div>
      </footer>
    </div>
  );
}
